export interface ModelPricingSnapshot {
  model_name?: string | null;
  model_api_name?: string | null;
  model_provider_name?: string | null;
  model_input_cost?: number | null;
  model_output_cost?: number | null;
  model_cost_threshold_tokens?: number | null;
  model_input_cost_above_threshold?: number | null;
  model_output_cost_above_threshold?: number | null;
  total_cost?: number;
  model_cost?: number;
  compute_cost?: number;
  execution_count?: number;
}

const formatUsdPerMillion = (value?: number | null): string => {
  if (value == null || Number.isNaN(Number(value))) return 'not recorded';
  return `$${Number(value).toFixed(2)}/1M`;
};

export const getModelDisplayName = (
  snapshot?: ModelPricingSnapshot | null,
  fallback = 'No model recorded',
): string => {
  return snapshot?.model_name || fallback;
};

export const formatModelPricing = (
  snapshot?: ModelPricingSnapshot | null,
): string => {
  if (!snapshot) return 'Input/output price not recorded';

  const base = `Input ${formatUsdPerMillion(snapshot.model_input_cost)}, output ${formatUsdPerMillion(snapshot.model_output_cost)}`;
  if (
    snapshot.model_cost_threshold_tokens &&
    snapshot.model_input_cost_above_threshold != null &&
    snapshot.model_output_cost_above_threshold != null
  ) {
    return `${base}; above ${snapshot.model_cost_threshold_tokens.toLocaleString()} tokens: input ${formatUsdPerMillion(snapshot.model_input_cost_above_threshold)}, output ${formatUsdPerMillion(snapshot.model_output_cost_above_threshold)}`;
  }

  return base;
};

export const formatModelSummary = (
  models?: ModelPricingSnapshot[] | null,
  fallback = 'No model recorded',
): string => {
  if (!models || models.length === 0) return fallback;
  if (models.length === 1) return getModelDisplayName(models[0], fallback);
  return `${getModelDisplayName(models[0], fallback)} +${models.length - 1} more`;
};

export const formatModelSummaryTooltip = (
  models?: ModelPricingSnapshot[] | null,
): string => {
  if (!models || models.length === 0) return 'Input/output price not recorded';
  return models
    .map((model) => {
      const runs = model.execution_count ? `, ${model.execution_count} runs` : '';
      return `${getModelDisplayName(model)}: ${formatModelPricing(model)}${runs}`;
    })
    .join('\n');
};

export const evaluatorModelSnapshot = (source: {
  evaluator_model_name?: string | null;
  evaluator_model_api_name?: string | null;
  evaluator_model_provider_name?: string | null;
  evaluator_model_input_cost?: number | null;
  evaluator_model_output_cost?: number | null;
  evaluator_model_cost_threshold_tokens?: number | null;
  evaluator_model_input_cost_above_threshold?: number | null;
  evaluator_model_output_cost_above_threshold?: number | null;
}): ModelPricingSnapshot => ({
  model_name: source.evaluator_model_name,
  model_api_name: source.evaluator_model_api_name,
  model_provider_name: source.evaluator_model_provider_name,
  model_input_cost: source.evaluator_model_input_cost,
  model_output_cost: source.evaluator_model_output_cost,
  model_cost_threshold_tokens: source.evaluator_model_cost_threshold_tokens,
  model_input_cost_above_threshold: source.evaluator_model_input_cost_above_threshold,
  model_output_cost_above_threshold: source.evaluator_model_output_cost_above_threshold,
});
